/**
 * タイピング測定の計算だけを置く。React も DOM も時計も触らない。
 *
 * 分けてあるのは、この数字がポートフォリオに「証明」として載るため。
 * 画面を動かさずに何度でも検算できないと、出した数字を信用できない。
 */

/** 5打鍵を1単語として数える。WPM の一般的な定義に合わせる */
export const KEYSTROKES_PER_WORD = 5;

/** 1回のテスト時間の既定値 */
export const TEST_DURATION_MS = 60_000;

/**
 * 選べる測定時間。
 *
 * 60 秒だと、速い人でも 300 打鍵ほどで終わる。指が温まる前に終わるので、
 * 息切れするのか持つのかが出ない。長い方を用意して、そこを見えるようにする。
 *
 * 長さが違う回を同じ土俵で比べない。kpm は速度なので割合としては比較できるが、
 * 3 分持たせるのと 1 分だけ飛ばすのは別の能力で、混ぜると 1 分の記録ばかりが
 * 上位に並ぶ。ランキングは長さごとに分けること。
 */
export const TYPING_DURATIONS_MS = [60_000, 120_000, 180_000] as const;

export type TypingDurationMs = (typeof TYPING_DURATIONS_MS)[number];

export function isTypingDuration(value: number): value is TypingDurationMs {
  return (TYPING_DURATIONS_MS as readonly number[]).includes(value);
}

/**
 * 実際に走りうる長さ。**選べる 3 つとは別物。**
 *
 * TYPING_DURATIONS_MS は「画面でコースとして選べる長さ」で、順位表の
 * 土俵もこれで分かれている。一方、重点練習の 90 秒はコースではない。
 * 選ばせないし、順位表にも載せない。それでも 1 回は走るし履歴には残る。
 *
 * **2 つを 1 つの型にまとめない。** まとめると、コースの釦が 4 つに増えるか、
 * 順位表が 90 秒の土俵を持つことになる。どちらも
 * docs/typing/battle-measurement-spec.md の表に無い
 */
export const TYPING_RUN_DURATIONS_MS = [
  60_000,
  90_000,
  120_000,
  180_000,
] as const;

export type TypingRunDurationMs = (typeof TYPING_RUN_DURATIONS_MS)[number];

export function isTypingRunDuration(
  value: number,
): value is TypingRunDurationMs {
  return (TYPING_RUN_DURATIONS_MS as readonly number[]).includes(value);
}

/** 「60秒」のような表示。秒と分を混ぜないため 60 で割り切れる時だけ分にする */
export function durationLabel(durationMs: number): string {
  const seconds = Math.round(durationMs / 1000);
  return seconds % 60 === 0 && seconds >= 120
    ? `${seconds / 60}分`
    : `${seconds}秒`;
}

/**
 * 測定時間のずれをどこまで許すか。requestAnimationFrame やタブの間引きで
 * 数百ミリ秒はずれる。厳密に 60000ms を求めると誰も認証を取れない
 */
export const DURATION_TOLERANCE_MS = 2_000;

/** これ未満の打鍵しかないセッションは認証に使わない。事故で始まっただけの回を弾く */
export const MIN_KEYSTROKES_FOR_CERTIFICATION = 60;

/** 苦手キーとして数え始めるまでの最低打鍵数。1回外しただけで苦手にしない */
export const MIN_ATTEMPTS_FOR_WEAK_KEY = 5;

export type Keystroke = {
  /** 押されたキー。修飾キーや制御キーは呼び出し側で落としてから渡す */
  key: string;
  correct: boolean;
  /** 打鍵時刻。間隔のばらつきを見るのに使う */
  at: number;
};

export type SessionInput = {
  keystrokes: Keystroke[];
  startedAt: number;
  endedAt: number;
  /** 貼り付けが起きたか */
  pasted: boolean;
  /** 測定中に画面から離れたか */
  blurred: boolean;
  /**
   * 何秒の測定だったか。省略時は 60 秒。
   *
   * 実際にかかった時間ではなく「そのつもりで始めた長さ」を渡す。
   * 実測と突き合わせて、途中で止めた回を弾くのに使う。
   */
  expectedDurationMs?: number;
};

export type SessionResult = {
  /** 1分間の打鍵数 */
  kpm: number;
  /** 5打鍵を1単語として換算した速度 */
  wpm: number;
  /** 正しい打鍵の割合。小数第1位まで */
  accuracy: number;
  /** 正確率を掛けた実効速度。速いが外すだけの人と分けるため */
  effectiveKpm: number;
  totalKeystrokes: number;
  missCount: number;
  durationMs: number;
  /** 打鍵間隔のばらつきから出す安定性。0 から 100 */
  stability: number;
  /** 同じミリ秒に重なった打鍵の数。サーバーが同じ判定をするために送る */
  simultaneousCount: number;
  /** 観測した最小の正の間隔。粗い時計では重なりを判定しない */
  clockMs: number;
  /** 記録に使える回か (貼り付けや離席の回は使えない) */
  valid: boolean;
  /** 使えないときの理由。画面に出して次はどうすればよいか伝えるため */
  invalidReason?: string;
};

export type WeakKey = {
  key: string;
  attempts: number;
  misses: number;
  /** 失敗率。小数第1位まで */
  missRate: number;
};

const round1 = (value: number) => Math.round(value * 10) / 10;

/*
人の手では出ない数。ここを超えた回は測定として成立していないと見なす。

**api-go の typingrank と同じ値を持つ。** 向こうは順位表に載せるかどうかを
見ていて、こちらは自己ベストとグラフと共有に出すかどうかを見ている。
片方だけに上限があると、順位表には載らないのに自己ベストと X の投稿には
人間離れした数字が乗る。両方に置く。

metrics.test.ts が typingrank.go を読んで、数がずれたら落とす。
*/

/*
コースの長さごとに上限を分ける。

**1 つの数で全部を見ると、短い回の速い人を弾く。** 60 秒は瞬発力で
押し切れるが、180 秒は指の持久力で頭打ちになる。同じ 700 を当てると、
3 分では緩く、1 分では本当に速い人の記録を「人の手では出ない」として
消してしまう。ここのコメント自身が人の記録を約 1,080 KPM と書いていた
のに 700 で切っていたので、根拠と値が噛み合っていなかった。

12 / 13 / 14 打鍵毎秒を上限に置いた。日本語ローマ字入力で 3 分間 12 打鍵
毎秒を保てる人は実在しないが、届かない上限を置くのが目的ではなく、
**自動化を弾きつつ人の記録は消さない**線を引くのが目的。
*/
export const MAX_CREDIBLE_KPM_BY_DURATION: Readonly<Record<number, number>> = {
  60_000: 840,
  120_000: 780,
  180_000: 720,
};

/** 表に無い長さは一番厳しい値で見る。緩い側に倒すと検査が素通りする */
export const STRICTEST_MAX_CREDIBLE_KPM = 720;

export function maxCredibleKpm(durationMs: number): number {
  return MAX_CREDIBLE_KPM_BY_DURATION[durationMs] ?? STRICTEST_MAX_CREDIBLE_KPM;
}

/**
 * 打鍵間隔がここまで揃うのは人ではない。
 *
 * 速度だけを見ると、ゆっくり一定で叩く自動化を通してしまう
 */
export const MAX_HUMAN_STABILITY = 97;

/*
同じミリ秒に重なってよい打鍵の割合 (千分率)。

最初 2% にしていたが、**それでは指紋対策を入れた Firefox の人を永久に
締め出す。** privacy.resistFingerprinting は時計を 100ms 粒度に丸めるので、
打鍵時刻が同じ値に落ちる。実測で 300 KPM の 4.1%、720 KPM の 24.5% が
重なり、どの速さで打っても失格になっていた。16.7ms 粒度でも 2% の予算の
半分を時計だけで食う。

**不正を 1 つ止めるために、正しく打っている人を消してはいけない。**

全部が同じ瞬間に入る回は間隔が 1 つも残らず安定性 0 になるので、
ERRATIC_STABILITY_FLOOR のほうが捕まえる。ここが受け持つのは半分だけ
重ねるような形なので、50% まで緩めても取りこぼさない。

千分率の整数で持つのは、小数だとサーバー側と丸めがずれるため。
*/
export const MAX_SIMULTANEOUS_PERMILLE = 500;

/*
ここ以上なら時計の粒度が粗いと見なす。

人の打鍵は速くても 80ms 空く。**最小の正の間隔がこれ以上ある回は、
重なりが打ち方ではなく時計の丸めで出ている。** 材料になっていないので
この検査自体をやらない。
*/
export const COARSE_CLOCK_MS = 8;

/**
 * 同じミリ秒に重なった打鍵の数と、その端末の時計の細かさを返す。
 *
 * clockMs は観測した最小の正の間隔。0 は正の間隔が 1 つも無かった
 * ということで、全打鍵が同じ瞬間に入った回を表す
 */
export function simultaneousOf(keystrokes: Keystroke[]): {
  overlapped: number;
  clockMs: number;
} {
  let overlapped = 0;
  let clockMs = 0;
  for (let i = 1; i < keystrokes.length; i++) {
    const gap = keystrokes[i].at - keystrokes[i - 1].at;
    if (gap <= 0) {
      overlapped++;
      continue;
    }
    if (clockMs === 0 || gap < clockMs) clockMs = gap;
  }
  return { overlapped, clockMs };
}

/** 重なりが多すぎるか。粗い時計では判定しない */
export function tooManySimultaneous(
  overlapped: number,
  totalKeystrokes: number,
  clockMs: number,
): boolean {
  if (totalKeystrokes < 3 || overlapped <= 0) return false;
  if (clockMs >= COARSE_CLOCK_MS) return false;
  return overlapped * 1_000 > MAX_SIMULTANEOUS_PERMILLE * (totalKeystrokes - 1);
}

export const ERRATIC_KPM_CEILING = 300;

/*
ここ以下を「揃っていない」と見なす。

**0 ちょうどで見ない。** 安定性はサーバーから見れば自己申告なので、
0 だけを弾く形にすると 1 と名乗るだけで素通りできる。3 分 2160 打鍵で
stability=1 を送ると、順位表で取れる最高点がそのまま通っていた。

10 は変動係数 0.9 にあたる。平均 200ms なら標準偏差 180ms ということで、
300 KPM 以上を保ちながらここまで散らばる打ち方は無い。
*/
export const ERRATIC_STABILITY_FLOOR = 10;


/**
 * 1回ぶんの結果を出す。
 *
 * 速度は打鍵数を実測時間で割る。「60秒テストだから 60 で割る」にしないのは、
 * 途中で止めた回や計測がずれた回で速度が水増しされるため
 */
/*
実効速度。**1 分あたりの当たった打鍵数**。

以前は 3 か所が別々に出していた。順位表は丸めた速度 × 生の正確率、
履歴は生 × 生、ここは丸め × 丸め。3 分の全域を総当たりすると
**約 23% の入力で順位表と履歴が 1 ずれた。** 同じ回なのに順位表と
グラフで数字が違う、が 4 回に 1 回起きていたということ。

速度と正確率を掛けるのをやめた。定義を展開すると

    (打鍵数 / 分) × ((打鍵数 - 誤打鍵) / 打鍵数) = (打鍵数 - 誤打鍵) / 分

で、割り算が 1 回に減る。**途中で丸める場所が無くなるので、掛ける順番の
違いで結果が割れようがない。** api-go の typinglimit と同じ式。
*/
export function effectiveKpmOf(
  totalKeystrokes: number,
  missCount: number,
  durationMs: number,
): number {
  if (durationMs <= 0 || totalKeystrokes <= 0) return 0;
  const hits = totalKeystrokes - missCount;
  if (hits <= 0) return 0;
  return Math.round((hits * 60_000) / durationMs);
}

export function summarizeSession(input: SessionInput): SessionResult {
  const durationMs = Math.max(0, input.endedAt - input.startedAt);
  const totalKeystrokes = input.keystrokes.length;
  const missCount = input.keystrokes.filter((k) => !k.correct).length;

  const minutes = durationMs / 60_000;
  const kpm = minutes > 0 ? Math.round(totalKeystrokes / minutes) : 0;
  const wpm = Math.round(kpm / KEYSTROKES_PER_WORD);
  const accuracy =
    totalKeystrokes > 0
      ? round1(((totalKeystrokes - missCount) / totalKeystrokes) * 100)
      : 100;
  const effectiveKpm = effectiveKpmOf(totalKeystrokes, missCount, durationMs);

  const stability = stabilityOf(input.keystrokes);
  const overlap = simultaneousOf(input.keystrokes);
  const { valid, invalidReason } = judgeValidity(
    input,
    durationMs,
    totalKeystrokes,
    stability,
  );

  return {
    kpm,
    wpm,
    accuracy,
    effectiveKpm,
    totalKeystrokes,
    missCount,
    durationMs,
    stability,
    simultaneousCount: overlap.overlapped,
    clockMs: overlap.clockMs,
    valid,
    ...(invalidReason ? { invalidReason } : {}),
  };
}

function judgeValidity(
  input: SessionInput,
  durationMs: number,
  totalKeystrokes: number,
  stability: number,
): { valid: boolean; invalidReason?: string } {
  if (input.pasted)
    return { valid: false, invalidReason: "貼り付けが使われた" };
  if (input.blurred)
    return { valid: false, invalidReason: "測定中に画面から離れた" };
  const expected = input.expectedDurationMs ?? TEST_DURATION_MS;
  // 長さそのものが決められた 3 つのどれかであること。ここを見ないと、
  // 6 秒を「6 秒の測定です」と名乗れば時間のずれの検査を素通りできる
  if (!isTypingDuration(expected)) {
    return { valid: false, invalidReason: "測定時間が決められた長さでない" };
  }
  if (Math.abs(durationMs - expected) > DURATION_TOLERANCE_MS) {
    return {
      valid: false,
      invalidReason: `測定時間が${durationLabel(expected)}になっていない`,
    };
  }
  // 長さに比例させる。固定の 60 打鍵だと、3 分で 60 打鍵しか打たなかった
  // 回まで「測定として成立した」ことになる。api-go 側も比例で見ている
  // **宣言した長さから出す。** 実測から出すと、少し短く測れた回の下限
  // だけが下がって api-go と判定が割れる。expected は 3 つのどれかに
  // 縛ってあるので、名乗るだけで緩めることはできない
  const minKeystrokes = Math.max(
    MIN_KEYSTROKES_FOR_CERTIFICATION,
    Math.round((MIN_KEYSTROKES_FOR_CERTIFICATION * expected) / 60_000),
  );
  const minutes = durationMs / 60_000;
  if (totalKeystrokes < minKeystrokes) {
    return { valid: false, invalidReason: "打鍵数が少なすぎる" };
  }
  // **四捨五入してから比べる。** api-go は Score.Kpm (丸めた値) と
  // 比べているので、生の値で比べるとちょうど境目で判定が食い違う。
  // 700.33 KPM がここでは無効、順位表では 700 として受理される
  const kpm = minutes > 0 ? Math.round(totalKeystrokes / minutes) : 0;
  // 上限は宣言した長さで引く。実測ではなく expected を使うのは、
  // 実測を短く名乗って上限を緩める抜け道を作らないため
  if (kpm > maxCredibleKpm(expected)) {
    return { valid: false, invalidReason: "人の手では出ない速さになっている" };
  }
  if (stability > MAX_HUMAN_STABILITY) {
    return { valid: false, invalidReason: "打鍵の間隔が揃いすぎている" };
  }
  // 揃いすぎの検査より後ろに置く。同じミリ秒に詰める回は安定性が 0 に
  // なって上の検査を抜けるので、抜けた先で捕まえる
  const overlap = simultaneousOf(input.keystrokes);
  if (
    tooManySimultaneous(overlap.overlapped, totalKeystrokes, overlap.clockMs)
  ) {
    return { valid: false, invalidReason: "同じ瞬間に打鍵が重なっている" };
  }
  if (stability <= ERRATIC_STABILITY_FLOOR && kpm >= ERRATIC_KPM_CEILING) {
    return { valid: false, invalidReason: "打鍵の間隔が測れていない" };
  }
  return { valid: true };
}

/**
 * 打鍵間隔のばらつきから安定性を出す。
 *
 * 変動係数 (標準偏差 / 平均) が 0 なら 100、1 以上なら 0。
 * 速さと別に見るのは、詰まりながら打つ人と一定で打つ人が
 * 同じ速度でも実務での疲れ方が違うため
 */
export function stabilityOf(keystrokes: Keystroke[]): number {
  if (keystrokes.length < 3) return 0;
  const intervals: number[] = [];
  for (let i = 1; i < keystrokes.length; i++) {
    const gap = keystrokes[i].at - keystrokes[i - 1].at;
    if (gap > 0) intervals.push(gap);
  }
  if (intervals.length < 2) return 0;

  const mean = intervals.reduce((sum, v) => sum + v, 0) / intervals.length;
  if (mean <= 0) return 0;
  const variance =
    intervals.reduce((sum, v) => sum + (v - mean) ** 2, 0) / intervals.length;
  const cv = Math.sqrt(variance) / mean;
  return Math.max(0, Math.min(100, Math.round((1 - cv) * 100)));
}

/**
 * 苦手キーを出す。
 *
 * 失敗率の高い順。同率なら打鍵数の多い順にするのは、たまたま外した1回より
 * 何度も外しているキーを先に練習させたいため。打鍵数が少ないキーは
 * そもそも候補にしない
 */
export function weakKeys(keystrokes: Keystroke[], limit = 5): WeakKey[] {
  const byKey = new Map<string, { attempts: number; misses: number }>();
  for (const stroke of keystrokes) {
    const entry = byKey.get(stroke.key) ?? { attempts: 0, misses: 0 };
    entry.attempts++;
    if (!stroke.correct) entry.misses++;
    byKey.set(stroke.key, entry);
  }

  return [...byKey.entries()]
    .filter(([, v]) => v.attempts >= MIN_ATTEMPTS_FOR_WEAK_KEY && v.misses > 0)
    .map(([key, v]) => ({
      key,
      attempts: v.attempts,
      misses: v.misses,
      missRate: round1((v.misses / v.attempts) * 100),
    }))
    .sort(
      (a, b) =>
        b.missRate - a.missRate ||
        b.attempts - a.attempts ||
        a.key.localeCompare(b.key),
    )
    .slice(0, limit);
}

/** 前回からの伸び。小数第1位までのパーセント。前回が0なら出さない */
export function growthRate(previous: number, current: number): number | null {
  if (previous <= 0) return null;
  return round1(((current - previous) / previous) * 100);
}
